import { CurrencyPipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { LocalStorageEnum, ModalEnum, PropertyHomeTypeEnum, PropertyTypeEnum } from '@enums';
import { getImageUrlDemo, getImageUrlOriginal, getImageUrlThumb, loadSavedHomes } from '@helpers';
import { IApiDataResponse, IPropertyDetailsModel, IPropertyListModel } from '@interfaces';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  injectDestroyService,
  ModalsService,
  PropertyDetailsService,
  provideDestroyService,
  SavedHomesService,
  SearchService,
  TakeATourService,
} from '@service';
import * as SavedHomesActions from '@store';
import { selectHeaderLang, selectHeaderLogged, selectSavedHomesIds } from '@store';
import { delay, filter, takeUntil } from 'rxjs/operators';
import { getEnumKeyByValue } from '../../helpers/get-enum-key-by-value.util';
import { mapperDemoPhotosUtilsSingle } from '../../helpers/mapper-demo-photos.utils';
import { IAvailableToursDto } from '../../interfaces/dto/available-tours.dto';
import { LocalStorageService } from '../../services/local-storage.service';

@Component({
  selector: 'app-property-details',
  templateUrl: './property-details.component.html',
  styleUrls: ['./property-details.component.scss'],
  providers: [provideDestroyService(), CurrencyPipe],
  standalone: false,
})
export class PropertyDetailsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = injectDestroyService();

  public propertyId: string;
  public property: IPropertyDetailsModel;
  public propertySimilar: IPropertyListModel[] = [];
  public takeTourList: IAvailableToursDto = null;
  public isInOneColum: boolean = false;
  public isLoggedUser: boolean = false;
  public thumbImages = [];
  public originalImages = [];
  public hasRealEmail: boolean = false;

  private hiddenProperties: number[] = this.ls.getItem(LocalStorageEnum.HIDDEN_PROPERTIES);
  private destroyRef = inject(DestroyRef);
  protected readonly ModalEnum = ModalEnum;

  @HostListener('window:resize')
  windowResize() {
    this.isInOneColum = window.innerWidth < 769;
  }

  constructor(
    private router: Router,
    private propertyDetails: PropertyDetailsService,
    private takeATour: TakeATourService,
    private searchService: SearchService,
    private savedHomesService: SavedHomesService,
    private modalsService: ModalsService,
    private store: Store,
    private ls: LocalStorageService,
    private currencyPipe: CurrencyPipe,
    private titleService: Title,
    private metaService: Meta,
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.store.pipe(select(selectHeaderLogged), takeUntil(this.destroy$), delay(100)).subscribe((res) => {
      this.isLoggedUser = res;

      if (this.isLoggedUser) {
        loadSavedHomes(this.savedHomesService, this.searchService, this.destroy$, this.store);
      }
    });

    this.getPropertyId();
    this.getAvailableTours();
    this.getProperty();
    this.windowResize();

    this.store.pipe(select(selectSavedHomesIds), takeUntil(this.destroy$)).subscribe((res) => {
      if (res.includes(+this.propertyId)) {
        this.property = { ...this.property, isSavedHome: true };
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        if (res['url'] && res['url'].includes('property') && res['url'].split('/')[2] !== this.propertyId) {
          this.getPropertyId();
          this.getProperty();
          // this.loadSimilarProperties();
        }
      });

    this.store
      .pipe(select(selectHeaderLang), takeUntilDestroyed(this.destroyRef))
      .subscribe((lng) => this.searchService.setPropertyCombinedDetails(this.property));
  }

  ngOnDestroy(): void {
    this.removeExistingLdJsonSchema();
  }

  public getPropertyId(): void {
    this.propertyId = this.router.url.split('/')[2];
    this.loadSimilarProperties();
  }

  public getProperty(): void {
    this.propertyDetails
      .getProperty(this.propertyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (property: IApiDataResponse<IPropertyDetailsModel>) => {
          this.property = { ...this.property, ...property.data };
          this.property = mapperDemoPhotosUtilsSingle(this.property);
          this.searchService.setPropertyCardInfoLine(this.property);

          this.hasRealEmail = !property.data?.broker?.email.includes('zexprivate');

          this.thumbImages = this.property.images?.length
            ? this.property.images.map((image: string) =>
                this.property.isDemoPhoto ? getImageUrlDemo(image) : getImageUrlThumb(image),
              )
            : [];
          this.originalImages = this.property.images?.length
            ? this.property.images.map((image: string) =>
                this.property.isDemoPhoto ? getImageUrlDemo(image) : getImageUrlOriginal(image),
              )
            : [];

          this.searchService.setPropertyCombinedDetails(this.property);

          // adding one copy of existing image to do a blured 3d tour invite
          if (this.property.has3DTour && this.property.images?.length) {
            this.property.images.push(this.property.images[0]);
          }

          if (this.hiddenProperties.includes(+this.propertyId)) {
            this.property = { ...this.property, isHidden: true };
          }

          this.generateSeoTags();
        },
        (error) => {
          error.status === 404 ? this.router.navigate(['/404']) : this.router.navigate(['/search']);
        },
      );
  }

  public getAvailableTours() {
    this.takeATour
      .getAvailableTours(+this.propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: IAvailableToursDto) => (this.takeTourList = data));
  }

  public loadSimilarProperties(): void {
    this.propertyDetails
      .getSimilarProperties(this.propertyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.propertySimilar = res?.data ?? [];
        this.propertySimilar.forEach((item) => {
          if (item.images?.length) {
            item.images[0] = getImageUrlThumb(item.images[0]);
          }
        });
      });
  }

  public openModal(modalId: ModalEnum): void {
    this.modalsService.open(modalId);
  }

  public saveHandler(state: boolean): void {
    if (state) {
      this.savedHomesService
        .add(this.property.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((_) => {
          this.property = { ...this.property, isSavedHome: true };
          this.store.dispatch(SavedHomesActions.addSavedHomes({ data: { ...this.property, isSavedHome: true } }));
        });
      return;
    }

    this.savedHomesService
      .delete(this.property.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => {
        this.property = { ...this.property, isSavedHome: false };
        this.store.dispatch(SavedHomesActions.removeSavedHomes({ data: this.property }));
      });
  }

  public hideHandler(isHidden: boolean): void {
    this.property = { ...this.property, isHidden };
    this.ls.updateItemsArray(LocalStorageEnum.HIDDEN_PROPERTIES, +this.propertyId, !isHidden);
  }

  public back() {
    this.router.navigate(['/search']);
  }

  public changePropertyHandler($event: any) {
    const headerElement = document.querySelector('#headerPropertyDetails');

    if (headerElement) {
      headerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (this.propertyId === $event) {
      return;
    }

    this.removeExistingLdJsonSchema();
    this.propertyId = $event;
    this.getProperty();
    this.loadSimilarProperties();
    this.getAvailableTours();
    this.cd.detectChanges();
  }

  private generateSeoTags(): void {
    if (this.property) {
      this.generateSeoTitle();
      this.generateSeoDescription();
      this.generateSeoImage();
      this.generateLdJsonSchema();
    }
  }

  private generateSeoTitle(): void {
    this.titleService.setTitle(this.property.seoTitle);
    this.metaService.updateTag({
      property: 'og:title',
      content: this.property.seoTitle,
    });
  }

  private generateSeoDescription(): void {
    const description = this.property.seoDescription?.length
      ? this.property.seoDescription
      : this.property.description || '';

    this.metaService.updateTag({
      name: 'description',
      content: description,
    });

    this.metaService.updateTag({
      property: 'og:description',
      content: description,
    });
  }

  private generateSeoImage(): void {
    const imageUrl = this.property.images?.length
      ? this.property.isDemoPhoto
        ? getImageUrlDemo(this.property.images[0])
        : getImageUrlOriginal(this.property.images[0])
      : null;

    if (imageUrl) {
      this.metaService.updateTag({
        property: 'og:image',
        content: imageUrl,
      });
    }
  }

  private removeExistingLdJsonSchema(): void {
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }
  }

  private generateLdJsonSchema(): void {
    const price = new Intl.NumberFormat('fr-FR').format(this.property.price);
    const imageUrl = this.property.images?.length
      ? this.property.isDemoPhoto
        ? getImageUrlDemo(this.property.images[0])
        : getImageUrlOriginal(this.property.images[0])
      : null;

    const propertyTypeName = this.translateService.instant(
      this.property.propertyType.toString() === PropertyTypeEnum.RENT
        ? 'SEO.property_type.rent'
        : 'SEO.property_type.sale',
    );

    const homeTypeName = this.translateService.instant(
      `SEO.home_type.${getEnumKeyByValue(PropertyHomeTypeEnum, this.property.homeType)}`,
    );

    const combinedDetails = this.property.combined_details?.map((item) => item.value).join(', ') || '';
    const hasEnoughDetails = combinedDetails.length > 50;
    const description = hasEnoughDetails ? combinedDetails : this.property.description || '';

    const ldJsonSchema = {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: `${propertyTypeName} ${this.property.rooms > 0 ? this.property.rooms + '-' + this.translateService.instant('SEO.rooms') + ' ' : ''}${homeTypeName}${this.property.address.city ? ' ' + this.translateService.instant('SEO.in_city') + ' ' + this.property.address.city : ''}`,
      description: description,
      url: `${window.location.origin}/property/${this.propertyId}/${this.property.slug}`,
      image: imageUrl,
      datePosted: this.property.createdAt,
      validThrough: this.property.updatedAt,
      priceCurrency: this.property.priceCurrency || 'EUR',
      price: this.property.price,
      address: {
        '@type': 'PostalAddress',
        streetAddress: this.property.address.street || '',
        addressLocality: this.property.address.city || '',
        addressCountry: this.property.address.country || '',
        postalCode: this.property.address.zipCode || '',
      },
      floorSize: this.property.homeSize
        ? {
            '@type': 'QuantitativeValue',
            value: this.property.homeSize,
            unitCode: 'MTK',
          }
        : undefined,
      numberOfRooms: this.property.rooms || undefined,
      numberOfBathroomsTotal: this.property.bathroom || undefined,
      numberOfBedrooms: this.property.bedroom || undefined,
      yearBuilt: this.property.yearBuild || undefined,
      offers: {
        '@type': 'Offer',
        priceCurrency: this.property.priceCurrency || 'EUR',
        price: this.property.price,
      },
    };

    Object.keys(ldJsonSchema).forEach((key) => {
      if (ldJsonSchema[key] === undefined) {
        delete ldJsonSchema[key];
      }
    });

    this.removeExistingLdJsonSchema();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ldJsonSchema);
    document.head.appendChild(script);
  }
}
